import { useQuery } from '@tanstack/react-query';
import { SourcingService } from 'api-clients/monolith';
import { useWatch } from 'react-hook-form';

import { useJobPostingContext } from '@/contexts/job-posting-context';
import { useBudgetType } from '@/hooks/use-budget-type';
import { queryKeys } from '@/query-keys';

import { ChannelFormState, OpeningInfo } from './types';

export interface OpeningView {
  id: string;
  title: string | undefined;
  /**
   * Channel form-state slices that have an entry for this opening in their
   * `openingBudgets`. Use this to render "channels selected for opening X".
   */
  selectedChannels: ChannelFormState[];
  /**
   * Sum of this opening's per-channel budgets across `selectedChannels`. `0`
   * when no channels are selected or all values are unset.
   */
  totalBudget: number;
}

/**
 * Single source of truth for "what does the user need to know about a given
 * opening in the job-posting flow?". Combines the static metadata seeded by
 * `openJobPostingForm` (title) with derived form-state views (selected
 * channels, opening-level budget).
 */
export function useOpening(openingId: string): OpeningView {
  const { openings, initialSelectedOpeningIds, hookForm } =
    useJobPostingContext();
  const { calculateTotalBudget } = useBudgetType();

  const stored: OpeningInfo | undefined = openings[openingId];

  // Backfill the opening title from the popular channels section
  const { data: popularData } = useQuery({
    queryKey: [
      ...queryKeys.jobPosting.channels.popular,
      initialSelectedOpeningIds,
      undefined,
      undefined,
    ],
    queryFn: () =>
      SourcingService.getInternalApiSourcingSourcingChannels(
        initialSelectedOpeningIds,
        undefined,
        'popular',
        undefined,
      ),
    enabled: false,
    staleTime: Infinity,
  });

  const apiTitle = stored?.title
    ? undefined
    : popularData?.selected_openings?.find(o => o.external_id === openingId)
        ?.title;

  const channels = useWatch({ control: hookForm.control, name: 'channels' });

  const selectedChannels = Object.values(channels ?? {}).filter(
    channel => openingId in channel.openingBudgets,
  );

  // Calculate the total budget for the opening based on the selected channels and their budgets
  const totalBudget = selectedChannels.reduce(
    (sum, channel) =>
      sum +
      calculateTotalBudget({
        budgetType: channel.budgetType,
        budgetPerUnit: channel.openingBudgets[openingId] ?? 0,
        startDate: channel.startsAt,
        endDate: channel.endAt,
      }),
    0,
  );

  return {
    id: openingId,
    title: stored?.title ?? apiTitle,
    selectedChannels,
    totalBudget,
  };
}
